import { Modal, Button, Form, InputNumber, Typography, message } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { useMainContext } from '../../hooks/useMainContext';
import './ObstacleForm.css';
import Draggable from 'react-draggable';
import { TbX, TbArrowsMove } from 'react-icons/tb';

export const EditObstaclePosForm = observer(() => {
    const { closetManager } = useMainContext();
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (closetManager.enableEditObstacleForm) {
            setModalPosition({ x: 0, y: 0 });
        }
    }, [closetManager.enableEditObstacleForm]);

    // Function to parse values like "0' 6.25"" into inches
    const parseInches = (value) => {
        if (typeof value !== 'string' || !value.trim()) {
            return 0; // Fallback for undefined, null, or empty
        }
        const match = value.match(/(\d+)' ([\d.]+)"/);
        return match ? parseFloat(match[2]) : 0;
    };

    // Extract limits as inches
    const topLimitInches = parseInches(
        closetManager.obstaclePosData.topLimit || "0' 0",
    );
    const leftLimitInches = parseInches(
        closetManager.obstaclePosData.leftLimit || "0' 0",
    );

    // Initialize form
    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: { top: 0, left: 0 },
    });

    // Update form values when data changes
    useEffect(() => {
        if (closetManager.obstaclePosData) {
            const top = parseInches(
                closetManager.obstaclePosData.top || "0' 0",
            );
            const left = parseInches(
                closetManager.obstaclePosData.left || "0' 0",
            );
            reset({
                top: parseFloat(top.toFixed(2)),
                left: parseFloat(left.toFixed(2)),
            });
        }
    }, [closetManager.obstaclePosData, reset]);

    // Handle inch input change to ensure 2 decimal places
    const handleInchChange = (field, value) => {
        const parsedValue = parseFloat(value) || 0;
        setValue(field, parseFloat(parsedValue.toFixed(2)));
    };

    // Validate position within limits
    const validatePosition = (field, limitInches) => {
        const value = parseFloat(watch(field));
        return (
            value <= limitInches ||
            `Value exceeds the limit of ${limitInches.toFixed(2)} inches`
        );
    };

    // Form submission handler
    const onSubmit = (data) => {
        const obstacleData = {
            top: `0' ${parseFloat(data.top).toFixed(2)}"`,
            left: `0' ${parseFloat(data.left).toFixed(2)}"`,
        };
        try {
            closetManager.updateObstaclePosition(obstacleData);
            closetManager.setEnableEditObstacleForm(false);
            closetManager.setSelectedObstacle(null);
            closetManager.drawNewClosets();
        } catch (error) {
            message.error(error.message);
        }
    };

    const closeModal = () => {
        closetManager.setEnableEditObstacleForm(false);
        reset();
    };

    return (
        <Modal
            className="obstacle-form-modal"
            width={500}
            styles={{
                content: {
                    padding: '32px',
                    borderRadius: '32px',
                },
            }}
            centered
            modalRender={(modal) => (
                <Draggable
                    handle=".drag-button"
                    cancel=".ant-input-number, .ant-btn:not(.drag-button)"
                    position={modalPosition}
                    onStop={(_, data) => {
                        setModalPosition({ x: data.x, y: data.y });
                    }}>
                    <div>{modal}</div>
                </Draggable>
            )}
            onCancel={closeModal}
            footer={null}
            closable={false}
            open={closetManager.enableEditObstacleForm}>
            <div className="obstacle-form-wrapper">
                <div
                    className="border-b-[1px] border-theme-border pb-2"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10,
                    }}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            fontSize: '32px',
                            lineHeight: '40px',
                            color: '#000000',
                        }}>
                        Edit Obstacle Position
                    </Typography.Title>
                    <div style={{ display: 'flex' }}>
                        <Button
                            className="drag-button flex items-center justify-center p-0 w-[32px] h-[32px]"
                            style={{ cursor: 'move', border: 'none' }}>
                            <TbArrowsMove size={20} />{' '}
                        </Button>
                        <Button
                            type="text"
                            className="flex items-center justify-center p-0 w-[32px] h-[32px]"
                            onClick={closeModal}>
                            <TbX size={20} />
                        </Button>
                    </div>
                </div>
                <Form
                    onFinish={handleSubmit(onSubmit)}
                    layout="vertical"
                    className="door-form !font-favorit obstacle-edit-form">
                    {/* Top Input */}
                    <Form.Item label="Top">
                        <Controller
                            name="top"
                            control={control}
                            rules={{
                                required: 'Required',
                                validate: () =>
                                    validatePosition('top', topLimitInches),
                            }}
                            render={({ field, fieldState }) => (
                                <>
                                    <InputNumber
                                        {...field}
                                        addonAfter='"'
                                        className="number-input"
                                        placeholder="inches"
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                        onChange={(value) =>
                                            handleInchChange('top', value)
                                        }
                                        status={
                                            fieldState.invalid ? 'error' : ''
                                        }
                                    />
                                    {fieldState.error && (
                                        <p className="error-message">
                                            {fieldState.error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    </Form.Item>

                    {/* Left Input */}
                    <Form.Item label="Left">
                        <Controller
                            name="left"
                            control={control}
                            rules={{
                                required: 'Required',
                                validate: () =>
                                    validatePosition('left', leftLimitInches),
                            }}
                            render={({ field, fieldState }) => (
                                <>
                                    <InputNumber
                                        {...field}
                                        controls={true}
                                        placeholder="inches"
                                        addonAfter='"'
                                        className="number-input"
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                        onChange={(value) =>
                                            handleInchChange('left', value)
                                        }
                                        status={
                                            fieldState.invalid ? 'error' : ''
                                        }
                                    />
                                    {fieldState.error && (
                                        <p className="error-message">
                                            {fieldState.error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    </Form.Item>

                    <div className="form-actions">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="bg-theme-orange text-theme-white !rounded-[36px] text-[16px] w-[140px] h-[40px] !!hover:bg-theme-orange-hover !shadow-none save-changes-button">
                            Confirm
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
});
